import {
  ChangePasswordRequest,
  ChangePasswordResponse,
  ConfirmAccountResponse,
  CreateFederationInviteRequest,
  CreateFederationInviteResult,
  CreatedFederationInvite,
  EditProfileResponse,
  EditProfileModel,
  ErrorContext,
  FederationInviteSettings,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  GetLoginStatusOptions,
  GetMeOptions,
  GetRegisterInitResponse,
  LoginConfigDto,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  LoginStatusResponse,
  MeResponse,
  MfaChallengeCancelResponse,
  MfaChallengeInitResponse,
  MfaChallengeResendResponse,
  MfaChallengeVerifyRequest,
  MfaChallengeVerifyResponse,
  MfaDisableResponse,
  MfaEnrollmentSendResponse,
  MfaEnrollmentVerifyResponse,
  MfaStatusResponse,
  RegisterInitCountry,
  RegisterRequest,
  RegisterResponse,
  ResetPasswordInitResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ResendVerificationMode,
  SetPasswordRequest,
  SetPasswordInitResponse,
  SetPasswordResponse,
  SupportEditAccountRequest,
  SupportEditAccountResponse,
  SupportResetPasswordLinkRequest,
  SupportResetPasswordLinkResponse,
  SupportSearchResponse,
  UiConfig,
} from './types';

let cachedCsrf: string | null = null;

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Reads a JSON body that the caller expects to be present even for error statuses
 * (these endpoints return `fieldErrors` on 400/401).
 *
 * A response that is not JSON means the request never reached the endpoint - an expired
 * cookie redirects to the HTML login page, and an antiforgery failure returns an empty
 * 400. Those must not be mistaken for a successful or well-formed reply.
 */
async function readJson<T>(res: Response, endpoint: string): Promise<T> {
  const contentType = res.headers.get('content-type') ?? '';

  if (!contentType.includes('application/json')) {
    throw new ApiError(res.status, `${endpoint} returned a non-JSON response (${res.status})`);
  }

  try {
    return (await res.json()) as T;
  } catch {
    throw new ApiError(res.status, `${endpoint} returned a malformed JSON body (${res.status})`);
  }
}

export async function getCsrfToken(): Promise<string> {
  if (cachedCsrf) return cachedCsrf;

  const res = await fetch('/api/csrf', { credentials: 'include' });
  if (!res.ok) throw new Error('CSRF fetch failed');

  const data = await res.json();
  cachedCsrf = data?.token ?? '';
  if (!cachedCsrf) throw new Error('No CSRF token in response');
  return cachedCsrf;
}

export async function login(req: LoginRequest): Promise<LoginResponse> {
  const doPost = async () => {
    const csrf = await getCsrfToken();
    return fetch('/api/account/login', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        RequestVerificationToken: csrf,
      },
      body: JSON.stringify(req),
    });
  };

  let res = await doPost();

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  if ((res.status === 403 || res.status === 400) && !isJson) {
    cachedCsrf = null;
    await getCsrfToken();
    res = await doPost();
  }

  // Let the UI read fieldErrors
  if (res.status === 400 || res.status === 401) return res.json();

  if (!res.ok) throw new Error(`Login failed (${res.status})`);
  return res.json();
}

export async function getLoginConfig(): Promise<LoginConfigDto> {
  const res = await fetch('/api/account/login-config', { credentials: 'include' });
  if (!res.ok) return { federatedLoginEnabled: false };
  return res.json();
}

export async function getMe(options: GetMeOptions = {}): Promise<MeResponse> {
  const { signal } = options;

  const res = await fetch('/api/account/me', {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!res.ok) {
    throw new Error(`GET /api/account/me failed: ${res.status}`);
  }

  return res.json();
}

export async function getLoginStatus(
  options: GetLoginStatusOptions = {},
): Promise<LoginStatusResponse> {
  const { returnUrl, signal } = options;

  const url = new URL('/api/account/login-status', window.location.origin);
  if (returnUrl) url.searchParams.set('returnUrl', returnUrl);

  const res = await fetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!res.ok) {
    throw new Error(`GET ${url.pathname} failed: ${res.status}`);
  }

  return res.json();
}

export async function logout(logoutId?: string): Promise<LogoutResponse> {
  const csrf = await getCsrfToken();

  const res = await fetch('/api/account/logout', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify({ logoutId }),
  });

  cachedCsrf = null;

  if (!res.ok) return {};
  return res.json();
}

export async function forgotPassword(req: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
  const res = await fetch('/api/account/forgot-password', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(req),
  });

  return res.json();
}

export async function getResetPasswordInit(
  userId: string,
  code: string,
): Promise<ResetPasswordInitResponse> {
  const qs = new URLSearchParams({ userId, code }).toString();
  const res = await fetch(`/api/account/reset-password?${qs}`, { credentials: 'include' });
  const data = (await res.json()) as ResetPasswordInitResponse;

  const { ok: dataOk, ...rest } = data;
  return { ...rest, ok: Boolean(res.ok && dataOk) };
}

export async function resetPassword(req: ResetPasswordRequest): Promise<ResetPasswordResponse> {
  const csrf = await getCsrfToken();

  const res = await fetch('/api/account/reset-password', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify(req),
  });

  const data = (await res.json()) as ResetPasswordResponse;
  const { ok: dataOk, ...rest } = data;
  return { ...rest, ok: Boolean(res.ok && dataOk) };
}

export async function confirmAccount(
  userId: string,
  code: string,
  newEmail?: string,
  returnUrl?: string,
): Promise<ConfirmAccountResponse> {
  const qs = new URLSearchParams();
  qs.set('userId', userId);
  qs.set('code', code);
  if (newEmail) qs.set('newEmail', newEmail);
  if (returnUrl) qs.set('returnUrl', returnUrl);

  const res = await fetch(`/api/account/confirm-account?${qs.toString()}`, {
    method: 'GET',
    credentials: 'include',
  });

  const data = (await res.json()) as ConfirmAccountResponse;
  const { ok: dataOk, ...rest } = data;
  return { ...rest, ok: Boolean(res.ok && dataOk) };
}

export async function resendVerificationEmail(
  email: string,
  returnUrl?: string,
  mode: ResendVerificationMode = 'registration',
  captchaToken?: string | null,
): Promise<void> {
  const csrf = await getCsrfToken();

  const res = await fetch('/api/account/resend-verification-email', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify({ email, returnUrl, mode, captcha: captchaToken ?? undefined }),
  });

  if (!res.ok) {
    throw new Error(`Failed to resend verification email (${res.status})`);
  }
}

export async function getTermsHtml(): Promise<string> {
  const res = await fetch('/api/terms', { credentials: 'include' });
  if (!res.ok) throw new Error(`Terms fetch failed (${res.status})`);
  return res.text();
}

const DEFAULT_UI_CONFIG: UiConfig = {
  maintenance: { display: false, level: 'info', time: '', date: '' },
};

export async function getUiConfig(): Promise<UiConfig> {
  const res = await fetch('/api/ui/config', { credentials: 'include' });
  if (!res.ok) return DEFAULT_UI_CONFIG;

  try {
    return (await res.json()) as UiConfig;
  } catch {
    return DEFAULT_UI_CONFIG;
  }
}

export async function getRegisterInit(): Promise<GetRegisterInitResponse> {
  const resp = await fetch('/api/account/register/init', {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });

  if (!resp.ok) {
    return { ok: false, status: resp.status };
  }

  const data = (await resp.json()) as {
    countries: RegisterInitCountry[];
    defaultCountryId: number;
  };

  return {
    ok: true,
    countries: data.countries ?? [],
    defaultCountryId: data.defaultCountryId,
  };
}

export async function register(req: RegisterRequest): Promise<RegisterResponse> {
  const doPost = async () => {
    const csrf = await getCsrfToken();

    return fetch('/api/account/register', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        RequestVerificationToken: csrf,
      },
      body: JSON.stringify({
        Email: req.email,
        ConfirmEmail: req.confirmEmail,
        FirstName: req.firstName,
        LastName: req.lastName,
        Password: req.password,
        ConfirmPassword: req.confirmPassword,
        SelectedCountryId: req.selectedCountryId,
        TermsOfServiceAccepted: req.termsOfServiceAccepted,
        ValidationField: req.validationField,
        ReturnUrl: req.returnUrl,
        PasswordScore: req.passwordScore,
        'g-recaptcha-response': req.captchaToken,
      }),
    });
  };

  let resp = await doPost();

  const ct = resp.headers.get('content-type') ?? '';
  const isJson = ct.includes('application/json');

  if ((resp.status === 403 || resp.status === 400) && !isJson) {
    cachedCsrf = null;
    await getCsrfToken();
    resp = await doPost();
  }

  let data: unknown;
  try {
    data = await resp.json();
  } catch {
    data = null;
  }

  const obj = typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : null;

  const redirectUrl = typeof obj?.redirectUrl === 'string' ? obj.redirectUrl : undefined;

  const fieldErrors =
    obj?.fieldErrors && typeof obj.fieldErrors === 'object'
      ? (obj.fieldErrors as Record<string, unknown>)
      : null;

  const fieldErrorsStr =
    fieldErrors != null
      ? Object.fromEntries(
          Object.entries(fieldErrors).filter(([, v]) => typeof v === 'string') as Array<
            [string, string]
          >,
        )
      : undefined;

  if (resp.ok) {
    return { ok: true, redirectUrl };
  }

  return { ok: false, fieldErrors: fieldErrorsStr };
}

export async function getErrorContext(): Promise<ErrorContext> {
  const res = await fetch('/api/system/error-context', {
    headers: { Accept: 'application/json' },
  });

  if (!res.ok) {
    throw new Error(`Failed to load error context (${res.status})`);
  }

  return (await res.json()) as ErrorContext;
}

export async function getSupportedCultures(): Promise<string[]> {
  const res = await fetch('/api/system/languages', { credentials: 'include' });
  if (!res.ok) throw new Error(`Languages fetch failed (${res.status})`);
  return res.json();
}

export async function getEditProfile(): Promise<EditProfileModel> {
  const [me, init, mfa] = await Promise.all([
    getMe(),
    getRegisterInit(),
    getMfaStatus().catch<MfaStatusResponse>(() => ({
      ok: false,
      available: false,
      enabled: false,
    })),
  ]);

  const countries = init.ok ? init.countries : [];
  const selectedCountryId = me.selectedCountryId ?? 0;

  return {
    email: me.email ?? '',
    firstName: me.firstName ?? '',
    lastName: me.lastName ?? '',
    selectedCountryId,
    isEmailConfirmed: Boolean(me.emailConfirmed),
    isFederated: Boolean(me.isFederated),
    countries,
    statusMessage: '',
    mfaAvailable: Boolean(mfa.available),
    mfaEnabled: Boolean(mfa.enabled),
  };
}

export async function getMfaStatus(): Promise<MfaStatusResponse> {
  const endpoint = '/api/manage/mfa';

  const res = await fetch(endpoint, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  const data = await readJson<{
    available?: boolean;
    enabled?: boolean;
  }>(res, 'GET /api/manage/mfa');

  const result = {
    ok: res.ok,
    available: Boolean(res.ok && data.available),
    enabled: Boolean(res.ok && data.enabled),
  };

  return result;
}

export async function sendMfaEnrollment(): Promise<MfaEnrollmentSendResponse> {
  const csrf = await getCsrfToken();

  const res = await fetch('/api/manage/mfa/enrollment/send', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify({}),
  });

  const data = await readJson<MfaEnrollmentSendResponse>(
    res,
    'POST /api/manage/mfa/enrollment/send',
  );

  return {
    ok: Boolean(res.ok && data.ok !== false),
    fieldErrors: data.fieldErrors,
  };
}

export async function verifyMfaEnrollment(code: string): Promise<MfaEnrollmentVerifyResponse> {
  const csrf = await getCsrfToken();

  const res = await fetch('/api/manage/mfa/enrollment/verify', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify({ code }),
  });

  const data = await readJson<MfaEnrollmentVerifyResponse>(
    res,
    'POST /api/manage/mfa/enrollment/verify',
  );

  return {
    ok: Boolean(res.ok && data.ok !== false),
    enabled: data.enabled,
    fieldErrors: data.fieldErrors,
  };
}

export async function disableMfa(): Promise<MfaDisableResponse> {
  const csrf = await getCsrfToken();

  const res = await fetch('/api/manage/mfa/disable', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify({}),
  });

  const data = await readJson<MfaDisableResponse>(res, 'POST /api/manage/mfa/disable');

  return {
    ok: Boolean(res.ok && data.ok !== false),
    enabled: data.enabled,
    fieldErrors: data.fieldErrors,
  };
}

export async function getMfaChallenge(
  returnUrl?: string,
  rememberMe?: boolean,
): Promise<MfaChallengeInitResponse> {
  const qs = new URLSearchParams();
  if (returnUrl) qs.set('returnUrl', returnUrl);
  if (rememberMe) qs.set('rememberMe', 'true');

  const url = qs.size > 0 ? `/api/account/mfa-challenge?${qs}` : '/api/account/mfa-challenge';

  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  const data = await readJson<MfaChallengeInitResponse>(res, 'GET /api/account/mfa-challenge');

  return {
    ok: Boolean(res.ok && data.ok !== false),
    maskedEmail: data.maskedEmail,
    returnUrl: data.returnUrl,
    rememberMe: Boolean(data.rememberMe ?? rememberMe),
  };
}

export async function verifyMfaChallenge(
  req: MfaChallengeVerifyRequest,
): Promise<MfaChallengeVerifyResponse> {
  const doPost = async () => {
    const csrf = await getCsrfToken();
    return fetch('/api/account/mfa-challenge/verify', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        RequestVerificationToken: csrf,
      },
      body: JSON.stringify(req),
    });
  };

  let res = await doPost();

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  if ((res.status === 403 || res.status === 400) && !isJson) {
    cachedCsrf = null;
    await getCsrfToken();
    res = await doPost();
  }

  if (res.status === 400 || res.status === 401) return res.json();

  if (!res.ok) throw new Error(`Mfa challenge verify failed (${res.status})`);
  return res.json();
}

export async function resendMfaChallenge(): Promise<MfaChallengeResendResponse> {
  const csrf = await getCsrfToken();

  const res = await fetch('/api/account/mfa-challenge/resend', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify({}),
  });

  const data = await readJson<MfaChallengeResendResponse>(
    res,
    'POST /api/account/mfa-challenge/resend',
  );

  return {
    ok: Boolean(res.ok && data.ok !== false),
    fieldErrors: data.fieldErrors,
  };
}

export async function cancelMfaChallenge(returnUrl?: string): Promise<MfaChallengeCancelResponse> {
  const csrf = await getCsrfToken();

  const res = await fetch('/api/account/mfa-challenge/cancel', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify({ returnUrl }),
  });

  const data = await readJson<MfaChallengeCancelResponse>(
    res,
    'POST /api/account/mfa-challenge/cancel',
  );

  return {
    ok: Boolean(res.ok && data.ok !== false),
    redirectUrl: data.redirectUrl,
  };
}

export async function updateProfile(req: {
  email: string;
  firstName: string;
  lastName: string;
  selectedCountryId: number;
  returnUrl?: string | null;
}): Promise<EditProfileResponse> {
  const csrf = await getCsrfToken();

  const res = await fetch('/api/manage/editprofile', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify(req),
  });

  return res.json();
}

export async function changePassword(
  payload: ChangePasswordRequest,
): Promise<ChangePasswordResponse> {
  const csrf = await getCsrfToken();

  const response = await fetch('/api/manage/changepassword', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify(payload),
  });

  const data = await readJson<ChangePasswordResponse>(response, 'POST /api/manage/changepassword');
  const { ok: dataOk, ...rest } = data;

  return {
    ...rest,
    ok: Boolean(response.ok && dataOk !== false),
  } as ChangePasswordResponse;
}

export async function setPassword(payload: SetPasswordRequest): Promise<SetPasswordResponse> {
  const csrf = await getCsrfToken();

  const response = await fetch('/api/manage/setpassword', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify(payload),
  });

  const data = await readJson<SetPasswordResponse>(response, 'POST /api/manage/setpassword');
  const { ok: dataOk, ...rest } = data;

  return {
    ...rest,
    ok: Boolean(response.ok && dataOk !== false),
  } as SetPasswordResponse;
}

export async function getSetPasswordInit(returnUrl?: string): Promise<SetPasswordInitResponse> {
  const qs = new URLSearchParams();
  if (returnUrl) qs.set('returnUrl', returnUrl);

  const url = qs.size > 0 ? `/api/manage/setpassword/init?${qs}` : '/api/manage/setpassword/init';
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  const data = await readJson<SetPasswordInitResponse>(
    response,
    'GET /api/manage/setpassword/init',
  );
  return {
    ok: Boolean(response.ok && data.ok !== false),
    hasPassword: Boolean(data.hasPassword),
    redirectUrl: data.redirectUrl,
  };
}

export async function supportSearch(search: string, skip = 0): Promise<SupportSearchResponse> {
  const qs = new URLSearchParams({ search, skip: String(skip) }).toString();
  const res = await fetch(`/api/support/search?${qs}`, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (!res.ok) {
    throw new Error(`Support search failed (${res.status})`);
  }

  return res.json();
}

export async function getSupportEditAccount(id: string): Promise<SupportEditAccountResponse> {
  const qs = new URLSearchParams({ id }).toString();
  const res = await fetch(`/api/support/edit-account?${qs}`, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  const data = await readJson<SupportEditAccountResponse>(res, 'GET /api/support/edit-account');

  return {
    ok: Boolean(res.ok && data.ok),
    account: data.account,
    redirectUrl: data.redirectUrl,
    fieldErrors: data.fieldErrors,
  };
}

export async function updateSupportEditAccount(
  req: SupportEditAccountRequest,
): Promise<SupportEditAccountResponse> {
  const csrf = await getCsrfToken();

  const res = await fetch('/api/support/edit-account', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify(req),
  });

  const data = await readJson<SupportEditAccountResponse>(res, 'POST /api/support/edit-account');

  return {
    ok: Boolean(res.ok && data.ok),
    account: data.account,
    redirectUrl: data.redirectUrl,
    fieldErrors: data.fieldErrors,
  };
}

export async function verifySupportEmail(id: string): Promise<SupportEditAccountResponse> {
  const csrf = await getCsrfToken();

  const res = await fetch('/api/support/verify-email', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify({ id }),
  });

  const data = await readJson<SupportEditAccountResponse>(res, 'POST /api/support/verify-email');

  return {
    ok: Boolean(res.ok && data.ok),
    account: data.account,
    redirectUrl: data.redirectUrl,
    fieldErrors: data.fieldErrors,
  };
}

export async function getSupportResetPasswordLink(
  req: SupportResetPasswordLinkRequest,
): Promise<SupportResetPasswordLinkResponse> {
  const csrf = await getCsrfToken();

  const res = await fetch('/api/support/reset-password-link', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify(req),
  });

  const data = await readJson<SupportResetPasswordLinkResponse>(
    res,
    'POST /api/support/reset-password-link',
  );

  return {
    ok: Boolean(res.ok && data.ok),
    redirectUrl: data.redirectUrl,
    fieldErrors: data.fieldErrors,
  };
}

/** Returns null when federation invites are not enabled in this environment (404). */
export async function getFederationInviteSettings(): Promise<FederationInviteSettings | null> {
  const res = await fetch('/api/support/federation-invites', {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (res.status === 404) return null;
  if (!res.ok)
    throw new ApiError(res.status, `GET /api/support/federation-invites failed (${res.status})`);

  return readJson<FederationInviteSettings>(res, 'GET /api/support/federation-invites');
}

export async function createFederationInvite(
  req: CreateFederationInviteRequest,
): Promise<CreateFederationInviteResult> {
  const csrf = await getCsrfToken();

  const res = await fetch('/api/support/federation-invites', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      RequestVerificationToken: csrf,
    },
    body: JSON.stringify(req),
  });

  // ASP.NET answers validation errors with application/problem+json.
  if (res.status === 400 && (res.headers.get('content-type') ?? '').includes('json')) {
    const problem = (await res.json()) as { errors?: Record<string, string[]> };
    return { ok: false, fieldErrors: problem.errors ?? {} };
  }

  if (!res.ok)
    throw new ApiError(res.status, `POST /api/support/federation-invites failed (${res.status})`);

  return {
    ok: true,
    invite: await readJson<CreatedFederationInvite>(res, 'POST /api/support/federation-invites'),
  };
}
